/**
 * VideoReviews — Phase 8 video deliverable, upgraded for the PIP lean rebuild
 * (WS1-T2 YouTube sweep + WS3 display-videos resolver).
 *
 * Data source:
 *   - fin_videos via page.tsx (Keepa HLS clips + model-verified YouTube rows
 *     from sprint-t2, all type-classified: review / tutorial / installation /
 *     comparison / unboxing / other).
 *
 * Selection:
 *   - "Featured" tab = getDisplayVideos (type diversity round-robin, max
 *     2/channel, cap 6) — same resolver the tests pin down.
 *   - Per-type tabs appear for types with >=1 video; tab lists sort by view
 *     count and cap at 12.
 *
 * Player strategy (conversion spec P0 — embed-only, zero exits):
 *   - YouTube rows (WS1-T2) embed on click via youtube-nocookie.com — the
 *     lite-embed pattern: no third-party JS/iframe until the user asks.
 *   - Amazon CDN HLS (.m3u8) rows keep the native <video> path (Safari only).
 *   - Rows that are neither are dropped from the pool — no external
 *     fallback links.
 *   - embed_html is never dangerouslySetInnerHTML'd (unsanitized).
 *
 * Brand policy:
 *   - source_url + video_url are INTERNAL fields; cards surface only title,
 *     channel, duration, views, and type. ASIN-shaped CDN URLs never print.
 *
 * Real-or-Hidden Rule 3: null/empty videos → null (DOM element absent).
 */

'use client'

import { useMemo, useState, useEffect } from 'react'
import { Play, Video } from 'lucide-react'
import { useIsHydrated } from '@/hooks/use-is-hydrated'
import {
  getDisplayVideos,
  isEmbeddableVideo,
  isHlsVideoUrl,
  safeThumbnailUrl,
  youtubeVideoId,
} from '@/lib/render-policy/display-videos'

export interface FinVideo {
  id: string
  fin: string
  source: string
  source_url: string | null
  video_url: string | null
  embed_html: string | null
  title: string | null
  channel: string | null
  duration_seconds: number | null
  thumbnail_url: string | null
  captured_at: string | null
  video_type?: string | null
  view_count?: number | null
}

interface VideoReviewsProps {
  videos: FinVideo[] | null | undefined
  className?: string
}

const TYPE_LABELS: Record<string, string> = {
  review: 'Reviews',
  tutorial: 'Tutorials',
  installation: 'Installation',
  comparison: 'Comparisons',
  unboxing: 'Unboxing',
}
const TAB_TYPE_ORDER = ['review', 'tutorial', 'installation', 'comparison', 'unboxing']
const TYPE_BADGES: Record<string, string> = {
  review: 'Review',
  tutorial: 'Tutorial',
  installation: 'Installation',
  comparison: 'Comparison',
  unboxing: 'Unboxing',
}
const PER_TAB_CAP = 12

function formatDuration(seconds: number | null): string {
  if (!seconds || seconds <= 0) return ''
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

function formatViews(views: number | null | undefined): string {
  if (typeof views !== 'number' || views <= 0) return ''
  try {
    return `${new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(views)} views`
  } catch {
    return `${views.toLocaleString()} views`
  }
}

/** Minimal capability check for native HLS playback (Safari only). */
function canPlayHlsNative(): boolean {
  if (typeof document === 'undefined') return false
  try {
    const v = document.createElement('video')
    return (
      v.canPlayType('application/vnd.apple.mpegurl') !== '' ||
      v.canPlayType('application/x-mpegURL') !== ''
    )
  } catch {
    return false
  }
}

const isHlsUrl = isHlsVideoUrl

interface VideoTileProps {
  video: FinVideo
  expanded: boolean
  onToggle: () => void
  hlsSupported: boolean
}

function VideoTile({ video, expanded, onToggle, hlsSupported }: VideoTileProps) {
  const duration = formatDuration(video.duration_seconds)
  const views = formatViews(video.view_count)
  const ytId = youtubeVideoId(video.video_url) ?? youtubeVideoId(video.source_url)
  const hlsPlayable = !!video.video_url && isHlsUrl(video.video_url) && hlsSupported
  const playable = !!ytId || hlsPlayable
  const sourceLink = video.source_url || video.video_url
  // FRIDGE-288: a data-URI thumbnail is never rendered — it would inline a
  // 4–10 KB base64 image per card. Null it so YouTube rows use the poster URL.
  const storedThumb = safeThumbnailUrl(video.thumbnail_url)
  const thumb = storedThumb || (ytId ? `https://i.ytimg.com/vi/${ytId}/hqdefault.jpg` : null)
  const typeBadge = video.video_type ? TYPE_BADGES[video.video_type] : undefined

  return (
    <div
      className="flex-none w-[260px] snap-start"
      data-testid="video-tile"
      data-source={video.source}
      data-video-type={video.video_type ?? undefined}
    >
      <div className="rounded-lg overflow-hidden border border-gray-200 bg-white">
        {expanded && ytId ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${ytId}?autoplay=1&rel=0`}
            title={video.title || 'Product video'}
            className="w-full aspect-video bg-black"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : expanded && hlsPlayable ? (
          <video
            src={video.video_url!}
            poster={storedThumb || undefined}
            controls
            playsInline
            preload="metadata"
            className="w-full aspect-video bg-black object-contain"
          />
        ) : (
          <button
            type="button"
            onClick={onToggle}
            className="relative w-full aspect-video bg-gray-100 group"
            // The badge and the duration are the button's visible text, so the
            // name carries them after the title, after a comma: axe-core 4.14.0's
            // label-content-name-mismatch (WCAG 2.5.3) drops parentheticals from
            // the name and wants the visible words as a contiguous run in it.
            aria-label={`Play ${video.title || 'product video'}${typeBadge || duration ? `, ${[typeBadge, duration].filter(Boolean).join(' ')}` : ''}`}
          >
            {thumb ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={thumb}
                alt={video.title || 'Product video thumbnail'}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-500">
                <Video className="w-10 h-10" />
              </div>
            )}
            <span className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
              <span className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-md">
                <Play className="w-5 h-5 text-gray-900 fill-current ml-0.5" aria-hidden="true" />
              </span>
            </span>
            {typeBadge && (
              <span className="absolute top-2 left-2 text-[10px] font-semibold uppercase tracking-wide text-white bg-black/60 px-1.5 py-0.5 rounded">
                {typeBadge}
              </span>
            )}
            {/* A real space between the badge and the duration, so their visible
                text reads "Review 0:49" and matches the name above. */}
            {typeBadge && duration ? ' ' : null}
            {duration && (
              <span className="absolute bottom-2 right-2 text-[11px] font-mono text-white bg-black/70 px-1.5 py-0.5 rounded">
                {duration}
              </span>
            )}
          </button>
        )}

        <div className="p-3">
          <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug min-h-[2.5rem]">
            {video.title || 'Product video'}
          </h3>
          <div className="mt-1 flex items-center justify-between gap-2 text-xs text-gray-500">
            <span className="truncate">{video.channel || 'Unknown'}</span>
            <span className="flex items-center gap-2 flex-shrink-0">
              {views && <span>{views}</span>}
              {duration && <span className="font-mono">{duration}</span>}
            </span>
          </div>
          {/* External watch-elsewhere fallback removed (conversion spec P0):
              a non-monetized exit. Pool is pre-filtered to embeddable rows
              (YouTube / HLS), so the fallback is near-unreachable. */}
        </div>
      </div>
    </div>
  )
}

export function VideoReviews({ videos, className = '' }: VideoReviewsProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<string>('featured')
  // Treat the runtime as HLS-capable only after we've actually checked (SSR
  // renders thumbnail+link mode so server HTML matches non-Safari first paint).
  // DERIVED after hydration rather than set from an effect
  // (react-hooks/set-state-in-effect). Still false for the server render and
  // the hydration pass, which is what keeps the server HTML matching non-Safari
  // first paint as the comment above requires.
  const hydrated = useIsHydrated()
  const hlsSupported = hydrated && canPlayHlsNative()

  // Embeddable-only pool (conversion spec P0): with the external "watch on
  // source" fallback gone, rows that can't play in-page (neither YouTube nor
  // Amazon HLS) would be dead tiles — drop them at selection time. The rule
  // lives in lib/render-policy/display-videos.ts (isEmbeddableVideo) and the
  // server applies it before serialization; this is the same rule kept here
  // so a caller that skips the server gate still renders honestly.
  const pool = useMemo(
    () =>
      (videos ?? []).filter(
        (v): v is FinVideo & { video_url: string } => !!v && isEmbeddableVideo(v)
      ),
    [videos]
  )

  const featured = useMemo(
    () =>
      getDisplayVideos(
        pool.map((v) => ({ ...v, video_type: v.video_type ?? null, view_count: v.view_count ?? null }))
      ),
    [pool]
  )

  const typeTabs = useMemo(() => {
    const counts = new Map<string, number>()
    for (const v of pool) {
      const t = v.video_type ?? 'other'
      counts.set(t, (counts.get(t) ?? 0) + 1)
    }
    return TAB_TYPE_ORDER.filter((t) => (counts.get(t) ?? 0) > 0).map((t) => ({
      type: t,
      label: TYPE_LABELS[t],
      count: counts.get(t)!,
    }))
  }, [pool])

  const shown = useMemo(() => {
    if (activeTab === 'featured') return featured
    return pool
      .filter((v) => (v.video_type ?? 'other') === activeTab)
      .sort((a, b) => (b.view_count ?? -1) - (a.view_count ?? -1))
      .slice(0, PER_TAB_CAP)
  }, [activeTab, featured, pool])

  if (pool.length === 0) return null

  return (
    <section
      className={`rounded-lg ${className}`}
      data-testid="video-reviews"
      data-video-count={pool.length}
      aria-label="Product videos"
    >
      <header className="flex items-end justify-between gap-3 mb-4">
        <div>
          <p className="text-[#ff9951] font-bold tracking-widest text-[11px] uppercase mb-1.5">
            Watch First
          </p>
          <div className="flex items-center gap-2">
            <h2 className="text-xl md:text-2xl font-black tracking-tighter text-gray-900">Product Videos</h2>
            <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full">
              {pool.length}
            </span>
          </div>
        </div>
        <p className="hidden sm:block text-xs text-gray-500 pb-1">
          Sourced and aggregated by Fridge.com.
        </p>
      </header>

      {typeTabs.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" role="tablist" aria-label="Video categories">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'featured'}
            onClick={() => { setActiveTab('featured'); setExpandedId(null) }}
            className={`text-xs font-medium px-2.5 py-1 rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0469f0]/40 ${
              activeTab === 'featured'
                ? 'bg-[#0469f0] border-[#0469f0] text-white'
                : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
            }`}
          >
            Featured
          </button>
          {typeTabs.map((t) => (
            <button
              key={t.type}
              type="button"
              role="tab"
              aria-selected={activeTab === t.type}
              onClick={() => { setActiveTab(t.type); setExpandedId(null) }}
              className={`text-xs font-medium px-2.5 py-1 rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0469f0]/40 ${
                activeTab === t.type
                  ? 'bg-[#0469f0] border-[#0469f0] text-white'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              {t.label} <span className="opacity-70">({t.count})</span>
            </button>
          ))}
        </div>
      )}

      <div
        className="flex overflow-x-auto gap-4 snap-x snap-mandatory pb-3"
        style={{ scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }}
      >
        {shown.map((v) => (
          <VideoTile
            key={v.id}
            video={v}
            expanded={expandedId === v.id}
            onToggle={() => setExpandedId(expandedId === v.id ? null : v.id)}
            hlsSupported={hlsSupported}
          />
        ))}
      </div>
    </section>
  )
}

export default VideoReviews
